import React, {useState} from "react";
import {useGetTicketByUniqueCode} from "../../hooks/Administrative/useGetTicketByUniqueCode";
import "./SupervisorTicketInfo.css";
import {format} from "date-fns";
import {es} from "date-fns/locale/es";
import {useGetTechnician} from "../../hooks/Supervisor/useGetTechnician";
import {useAssignTicket} from "../../hooks/Supervisor/useAssignTicket";

const ticketStateMap: Map<string, string> = new Map([
    ["CREATED", "Creado"],
    ["APPROVED", "Aprobado"],
    ["ASSIGNED", "Asignado"],
    ["IN_PROGRESS", "En progreso"],
    ["BLOCKED", "Bloqueado"],
    ["IN_REVIEW", "En revisión"],
    ["COMPLETED", "Completado"],
]);

const serviceTypeMap: Map<string, string> = new Map([
    ["PREVENTIVE", "Preventivo"],
    ["CORRECTIVE", "Correctivo"],
    ["INSTALLATION", "Instalación"],
    ["VERIFICATION", "Verificación"],
]);

const SupervisorTicketInfo = ({additionalInfo, ticketCode, onBack, showTicketInfo, updateTicketStateLocally}: {
    additionalInfo: any,
    ticketCode: string,
    onBack: () => void,
    showTicketInfo: (show: boolean) => void,
    updateTicketStateLocally: (newState: any, ticketUniqueCode: any) => void
}) => {
    const {ticket, updateTicketTechnicianLocally} = useGetTicketByUniqueCode(ticketCode);
    const [isModalVisible, setModalVisible] = useState(false);
    const {technicians, isLoading} = useGetTechnician();
    const [selectedTechnician, setSelectedTechnician] = useState<string>("");
    const [selectedTechnicianName, setSelectedTechnicianName] = useState<string>("");
    const {assignTicketToTechnician, assigningTechnician} = useAssignTicket()
    const formattedDate = ticket?.createdAt
        ? format(new Date(ticket.createdAt.replace(" ", "T")), "dd MMMM yyyy, HH:mm:ss", {locale: es})
        : "N/A";

    const handleAssignTicketIcon = () => {
        setModalVisible(true);
    };

    const closeAssignModal = () => {
        setModalVisible(false);
    }

    const confirmTechnicianForTicket = () => {
        assignTicketToTechnician(selectedTechnician, ticketCode).then(
            () => {
                updateTicketTechnicianLocally(
                    selectedTechnician && selectedTechnicianName
                        ? `${selectedTechnicianName} - ${selectedTechnician}`
                        : ""
                );
                closeAssignModal();
                const newState = (selectedTechnician !== "" && selectedTechnicianName !== "")
                    ? "Asignado"
                    : "Aprobado";

                updateTicketStateLocally(newState, ticketCode);
            }
        );
    }

    return (
        <div className="tickets-info-view-container">
            <div className="tickets-info-view-header">
                <button onClick={onBack}>REGRESAR</button>
                <div className="tickets-info-view-header-title">
                    <p>TICKET</p>
                </div>
                {(ticket?.ticketState === "APPROVED" || ticket?.ticketState === "ASSIGNED") && (
                    <>
                        <span className="material-icons-outlined icon-unselected" onClick={handleAssignTicketIcon}>
                            person
                        </span>
                    </>
                )}
            </div>
            <div className="admin-tickets-info-wrapper">
                <div className="tickets-info-view-body-info">
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLAVE DE TICKET</div>
                        <div className="value-label">{ticketCode}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TIPO DE SERVICIO</div>
                        <div className="value-label">{serviceTypeMap.get(ticket?.service)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">ESTADO</div>
                        <div className="value-label">{ticketStateMap.get(ticket?.ticketState)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">DESCRIPCION</div>
                        <div className="value-label">{ticket?.description}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">COMENTARIO ADICIONAL</div>
                        <div className="value-label">{ticket?.additionalComment}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TÉCNICO ASIGNADO</div>
                        <div className="value-label">{ticket?.assignedTechnician}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE CREACIÓN</div>
                        <div className="value-label">{formattedDate}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLIENTE</div>
                        <div className="value-label">{additionalInfo.CLIENTE}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">PLANTA</div>
                        <div className="value-label">{additionalInfo.PLANTA}</div>
                    </div>
                </div>
                <div className="admin-tickets-additional-info">
                    <div className="ticket-additional-info-attribute">
                        <div className="attribute-label">EQUIPOS</div>
                        <div>
                            <ul className="additional-info-attribute-list">
                                {ticket?.equipments?.map((equipment: any, index: any) => (
                                    <li key={index}>
                                        {equipment}
                                    </li>))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {isModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>Asignar técnico a ticket</p>
                        <select
                            id="atc"
                            value={selectedTechnician}
                            onChange={(e) => {
                                const selectedUsername = e.target.value;
                                const selectedName = technicians.find((technician: any) => technician.username === selectedUsername);
                                setSelectedTechnician(selectedUsername);
                                setSelectedTechnicianName(selectedName ? selectedName.fullName : "");
                            }}
                            style={{
                                border: "1px solid black",
                                backgroundColor: "white"
                            }}
                        >
                            <option value=""></option>
                            {technicians.map((option: any) => (
                                <option key={option.username} value={option.username}>
                                    {option.fullName} - {option.username}
                                </option>
                            ))}
                        </select>
                        <div className="modal-buttons">
                            <button
                                onClick={confirmTechnicianForTicket}
                                disabled={assigningTechnician}
                                className={assigningTechnician ? "disabled-button" : ""}
                            >
                                Aceptar
                            </button>
                            <button
                                onClick={closeAssignModal}
                                disabled={assigningTechnician}
                                className={assigningTechnician ? "disabled-button" : ""}
                            >
                                Cancelar
                            </button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default SupervisorTicketInfo;
